import React, { useState } from 'react';
import { Quiz } from '../types';
import { CheckCircle, XCircle, ChevronRight, RefreshCw, X } from 'lucide-react';

interface QuizModalProps {
  quiz: Quiz;
  onClose: () => void;
}

export const QuizModal: React.FC<QuizModalProps> = ({ quiz, onClose }) => {
  const [currentQuestionIdx, setCurrentQuestionIdx] = useState(0);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [isAnswered, setIsAnswered] = useState(false);
  const [score, setScore] = useState(0);
  const [showResults, setShowResults] = useState(false);

  const currentQuestion = quiz.questions[currentQuestionIdx];

  const handleOptionClick = (idx: number) => {
    if (isAnswered) return;
    setSelectedOption(idx);
    setIsAnswered(true);
    if (idx === currentQuestion.correctAnswerIndex) {
      setScore(s => s + 1);
    }
  };

  const nextQuestion = () => {
    if (currentQuestionIdx < quiz.questions.length - 1) {
      setCurrentQuestionIdx(p => p + 1);
      setSelectedOption(null);
      setIsAnswered(false);
    } else {
      setShowResults(true);
    }
  };

  if (showResults) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
        <div className="bg-white dark:bg-slate-800 rounded-2xl max-w-md w-full p-8 text-center shadow-2xl border dark:border-slate-700">
          <h2 className="text-3xl font-bold mb-4 dark:text-white">Quiz Complete!</h2>
          <div className="text-6xl font-black mb-6 text-blue-600 dark:text-blue-400">
            {Math.round((score / quiz.questions.length) * 100)}%
          </div>
          <p className="text-gray-600 dark:text-gray-300 mb-8">
            You got {score} out of {quiz.questions.length} questions correct.
          </p>
          <button 
            onClick={onClose}
            className="w-full py-3 bg-gray-900 dark:bg-white dark:text-slate-900 text-white rounded-xl font-bold hover:opacity-90 transition-opacity"
          >
            Back to Notes
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl max-w-2xl w-full flex flex-col shadow-2xl border dark:border-slate-700 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b dark:border-slate-700 flex justify-between items-center bg-gray-50 dark:bg-slate-900">
          <div>
            <span className="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase tracking-widest">
              Question {currentQuestionIdx + 1} of {quiz.questions.length}
            </span>
            <h3 className="text-xl font-bold mt-1 dark:text-white">Knowledge Check</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-200 dark:hover:bg-slate-700 rounded-full transition-colors">
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-8 overflow-y-auto">
          <h2 className="text-xl font-medium mb-6 leading-relaxed dark:text-gray-100">
            {currentQuestion.question}
          </h2>

          <div className="space-y-3">
            {currentQuestion.options.map((option, idx) => {
              let btnClass = "border-2 border-gray-200 dark:border-slate-700 hover:border-blue-400 dark:hover:border-blue-500 bg-white dark:bg-slate-800";
              let icon = null;

              if (isAnswered) {
                if (idx === currentQuestion.correctAnswerIndex) {
                  btnClass = "border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300";
                  icon = <CheckCircle size={20} className="text-green-600" />;
                } else if (idx === selectedOption) {
                  btnClass = "border-red-500 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300";
                  icon = <XCircle size={20} className="text-red-600" />;
                } else {
                  btnClass = "opacity-50 border-gray-200 dark:border-slate-700";
                }
              }

              return (
                <button
                  key={idx}
                  onClick={() => handleOptionClick(idx)}
                  disabled={isAnswered}
                  className={`w-full text-left p-4 rounded-xl transition-all duration-200 flex items-center justify-between ${btnClass}`}
                >
                  <span className="font-medium dark:text-gray-200">{option}</span>
                  {icon}
                </button>
              );
            })}
          </div>

          {isAnswered && (
            <div className="mt-6 p-4 bg-blue-50 dark:bg-slate-700/50 rounded-lg border border-blue-100 dark:border-slate-600 animate-in fade-in slide-in-from-bottom-2">
              <h4 className="font-bold text-blue-800 dark:text-blue-300 mb-1 text-sm">Explanation</h4>
              <p className="text-blue-900 dark:text-gray-300 text-sm leading-relaxed">
                {currentQuestion.explanation}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t dark:border-slate-700 bg-gray-50 dark:bg-slate-900 flex justify-end">
          <button
            onClick={nextQuestion}
            disabled={!isAnswered}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl font-bold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-700 transition-colors"
          >
            {currentQuestionIdx === quiz.questions.length - 1 ? 'Finish' : 'Next Question'}
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};